import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMyMaintenanceInfaqs } from '../../hooks/useMaintenanceInfaq';
import type { MaintenanceInfaq } from '../../services/maintenance-infaq.service';

const STATUS_TABS = [
  { key: '', label: 'All' },
  { key: 'PENDING_REVIEW', label: 'Pending review' },
  { key: 'OPEN', label: 'Open' },
  { key: 'CLOSED', label: 'Closed' },
  { key: 'COMPLETED', label: 'Completed' },
  { key: 'REJECTED', label: 'Rejected' },
  { key: 'WITHDRAWN', label: 'Withdrawn' },
];

const STATUS_COLORS: Record<string, string> = {
  PENDING_REVIEW: 'bg-amber-100 text-amber-800',
  OPEN: 'bg-green-100 text-green-800',
  CLOSED: 'bg-blue-100 text-blue-800',
  COMPLETED: 'bg-emerald-100 text-emerald-800',
  REJECTED: 'bg-red-100 text-red-800',
  WITHDRAWN: 'bg-neutral-100 text-neutral-700',
};

export default function ProviderInfaqPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [status, setStatus] = useState('');
  const { data, isLoading } = useMyMaintenanceInfaqs(status || undefined);

  const campaigns = data ?? [];

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-6">
      <header>
        <h1 className="text-2xl font-semibold text-neutral-900">
          {t('maintenanceInfaq.navTitle', { defaultValue: 'Maintenance Infaq' })}
        </h1>
        <p className="mt-1 text-sm text-neutral-600">
          {t('maintenanceInfaq.navSubtitle', {
            defaultValue:
              'Donation campaigns raised against your vehicles under maintenance.',
          })}
        </p>
      </header>

      <div className="flex flex-wrap gap-2">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.key || 'all'}
            onClick={() => setStatus(tab.key)}
            className={`rounded-full px-3 py-1 text-xs font-medium ${
              status === tab.key
                ? 'bg-primary-600 text-white'
                : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="rounded-md border border-neutral-200 bg-white p-8 text-center text-sm text-neutral-500">
          Loading…
        </div>
      ) : campaigns.length === 0 ? (
        <div className="rounded-md border border-dashed border-neutral-300 bg-white p-12 text-center">
          <p className="text-sm text-neutral-600">
            {t('maintenanceInfaq.empty', {
              defaultValue: 'No campaigns yet.',
            })}
          </p>
          <p className="mt-1 text-xs text-neutral-500">
            {t('maintenanceInfaq.emptyHint', {
              defaultValue:
                'Set an asset to Maintenance from the Assets page to start a campaign.',
            })}
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-md border border-neutral-200 bg-white">
          <table className="min-w-full divide-y divide-neutral-200 text-sm">
            <thead className="bg-neutral-50 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
              <tr>
                <th className="px-4 py-3">Title</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Raised / Target</th>
                <th className="px-4 py-3 text-center">Days left</th>
                <th className="px-4 py-3">Invoice</th>
                <th className="px-4 py-3">Closing</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {campaigns.map((c: MaintenanceInfaq) => {
                const raised = Number(c.raisedAmount || 0);
                const target = Number(c.targetAmount || 0);
                const daysLeft =
                  c.status === 'OPEN' && c.maxEndDate
                    ? Math.max(
                        0,
                        Math.ceil(
                          (new Date(c.maxEndDate).getTime() - Date.now()) /
                            (1000 * 60 * 60 * 24),
                        ),
                      )
                    : null;
                return (
                  <tr
                    key={c.id}
                    onClick={() => navigate(`/provider-dashboard/infaq/${c.id}`)}
                    className="cursor-pointer hover:bg-neutral-50"
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-neutral-900">{c.title}</div>
                      <div className="text-xs text-neutral-500">
                        {c.provider?.companyName ?? '—'}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${
                          STATUS_COLORS[c.status] ?? 'bg-neutral-100'
                        }`}
                      >
                        {c.status.replace(/_/g, ' ').toLowerCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      <div className="font-medium">RM {raised.toFixed(2)}</div>
                      <div className="text-xs text-neutral-500">
                        of RM {target.toFixed(2)}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center tabular-nums">
                      {daysLeft === null ? '—' : daysLeft}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {c.requiresInvoice ? (
                        <span className="text-amber-700">Tier required</span>
                      ) : (
                        <span className="text-neutral-500">Not required</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {c.status === 'CLOSED' ? (
                        c.closingStatus ? (
                          <span
                            className={
                              c.closingStatus === 'APPROVED'
                                ? 'text-emerald-700'
                                : c.closingStatus === 'REJECTED'
                                ? 'text-red-700'
                                : 'text-amber-700'
                            }
                          >
                            {c.closingStatus}
                          </span>
                        ) : (
                          <span className="font-medium text-blue-700">
                            Submit report →
                          </span>
                        )
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
